import { Inline, vars } from '@etg/wings';
import { css, cx, resetButtonStyle } from '@eti/styles';
import { CaretRight } from '@phosphor-icons/react';
import { useDirection, useSnowplowTracking } from '@eti/providers';
import { Overlay } from '@eti/components';
import { forwardRef, useState, type ReactNode } from 'react';
import { useScrollToElementViaQueryParams } from '../../../../common/custom-hooks/scrollToElementViaQueryParams';
import { focusStyles } from '../../../../utils/styleUtils';
import type { SnowplowEventDataType } from '../../models';

const buttonStyles = (isDisabled: boolean, shouldAddBorder: boolean) => css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  background-color: ${isDisabled ? '#eee' : '#fff'};
  border: ${shouldAddBorder ? `1px solid ${vars.colors.divider}` : 'none'};
  border-radius: ${vars.button.borderRadius};
  cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
  padding: 16px;
  text-align: start;
  width: 100%;

  :active {
    border-color: ${vars.colors.inputs.main};
  }

  :active,
  :hover {
    background-color: ${isDisabled ? '#eee' : vars.colors.inputs.hover};
  }
`;

const contentStyles = css`
  flex: 1;
`;

interface CaretButtonWithDrawerProps {
  children: ReactNode;
  className?: string;
  dataTestId: string;
  drawerTitle: string;
  drawerContent: ReactNode;
  isDisabled?: boolean;
  queryParam?: string;
  onClick?: () => void;
  snowplow?: SnowplowEventDataType;
  shouldAddBorder?: boolean;
}

const CaretButtonWithDrawer = forwardRef<HTMLButtonElement, CaretButtonWithDrawerProps>(
  (
    {
      children,
      className,
      dataTestId,
      drawerTitle,
      drawerContent,
      isDisabled = false,
      queryParam,
      onClick,
      snowplow,
      shouldAddBorder = true,
    },
    ref?,
  ) => {
    const { isRTL } = useDirection();
    const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
    const [isOpen, setIsOpen] = useState(false);

    useScrollToElementViaQueryParams(dataTestId, queryParam, () => {
      setIsOpen(true);
    });

    const handleOnClick = () => {
      if (isSnowplowTrackingEnabled && snowplow?.label && snowplow.category) {
        sendTrackEvent({
          category: snowplow.category,
          action: 'Click',
          label: snowplow.label,
          property: snowplow.property,
        });
      }
      if (onClick) {
        onClick();
      }
      setIsOpen(true);
    };

    return (
      <>
        <button
          aria-controls={`${dataTestId}-drawer`}
          aria-expanded={isOpen ? 'true' : 'false'}
          aria-haspopup="dialog"
          className={cx(buttonStyles(isDisabled, shouldAddBorder), focusStyles, className)}
          data-testid={dataTestId}
          disabled={isDisabled}
          id={dataTestId}
          onClick={handleOnClick}
          ref={ref}
          type="button"
        >
          <Inline alignY="center" noWrap>
            <div className={contentStyles}>{children}</div>
            <CaretRight aria-hidden="true" color="#262626" mirrored={isRTL} size={24} />
          </Inline>
        </button>
        <Overlay.Drawer
          dataTestId={`${dataTestId}-drawer`}
          handleClose={() => setIsOpen(false)}
          id={`${dataTestId}-drawer`}
          isOpen={isOpen}
        >
          <Overlay.Header onClose={() => setIsOpen(false)} title={drawerTitle} />
          <Overlay.Content data-testid={`${dataTestId}-drawer-content`}>
            {drawerContent}
          </Overlay.Content>
        </Overlay.Drawer>
      </>
    );
  },
);

export default CaretButtonWithDrawer;
